import { type FC } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCountryAccessesSelectQuery } from "@/partner/entities/countryAccesses";

interface CountryAccessOption {
  value: string;
  label: string;
}

interface CountryAccessSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
}

/**
 * Контролируемый (не react-final-form Field) вариант селекта шаблона
 * гео-доступа — переиспользует хук фичи countryAccesses. Используется внутри
 * PriceEditDialog, где цена редактируется как локальная копия объекта, а не
 * как поле формы (см. features/videos/ui/CountryAccessField.tsx — та версия
 * жёстко привязана к react-final-form полю "countryAccessId" и не подходит
 * для вложенного редактирования одной цены). Пустая опция = «действует
 * шаблон по умолчанию» (countryAccessId: null).
 *
 * référence: components/CountryAccessField/CountryAccessField.js —
 * Autocomplete, не TextField select (п.11 аудита).
 */
export const CountryAccessSelect: FC<CountryAccessSelectProps> = ({
  value,
  onChange,
  label,
}) => {
  const { t } = useTranslation();
  const { data, loading } = useCountryAccessesSelectQuery();
  const templates = data?.countryAccesses?.nodes ?? [];

  // В отличие от Content (CountryAccessField.tsx), у Product-цены без явного
  // countryAccessId нет фолбэка на дефолтный шаблон платформы — бэкенд
  // (ProductService.denormalizePrices) оставляет её вообще без ограничений,
  // независимо от того, есть ли у платформы дефолтный шаблон и как он
  // называется (найдено при аудите: раньше здесь дублировался текст
  // Content-варианта "По умолчанию (Название)", хотя реального наследования
  // правил для цены не происходит). Поэтому пустая опция всегда означает
  // «не ограничено», а не «действует шаблон X».
  const defaultOption: CountryAccessOption = {
    value: "",
    label: t("products.priceEditDialog.countryAccessDefaultOptionEmpty"),
  };

  const options: CountryAccessOption[] = [
    defaultOption,
    ...templates.map((item) => ({ value: item.id, label: item.title })),
  ];

  const selected =
    options.find((option) => option.value === (value || "")) ?? defaultOption;

  return (
    <Autocomplete
      loading={loading}
      options={options}
      value={selected}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      getOptionLabel={(option) => option.label}
      onChange={(_event, newValue) => onChange(newValue?.value || null)}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          variant="outlined"
          label={label ?? t("products.priceEditDialog.countryAccessLabel")}
        />
      )}
    />
  );
};
